<template>
  <div class="admin-versions-page">
    <div class="container mt-4 admin-versions-container">
      <div class="header-section mb-4">
        <div class="header-title-section mb-3">
          <h2 class="header-title">Administration des versions</h2>
        </div>
        <div class="header-actions-section">
          <button @click="openCreateModal" class="btn btn-primary new-version-btn">
            <i class="fas fa-plus me-2"></i>Nouvelle version
          </button>
        </div>
      </div>

      <!-- Liste des versions existantes -->
      <div class="versions-list">
        <div v-if="loading" class="text-center">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Chargement...</span>
          </div>
        </div>

        <div v-else-if="versions.length === 0" class="alert alert-info">
          Aucune version trouvée.
        </div>

        <div v-else class="version-items">
          <div 
            v-for="version in versions" 
            :key="version.id" 
            class="version-item card mb-3"
          >
            <div class="card-body">
                             <div class="version-content">
                 <div class="version-header">
                   <h5 class="card-title mb-1">
                     {{ version.title }}
                     <span :class="getStatusBadgeClass(version.publié)" class="badge ms-2">
                       {{ version.publié ? 'Publiée' : 'Brouillon' }}
                     </span>
                   </h5>
                   <p class="text-muted mb-2">
                     Version {{ version.version_number }}
                     <br>
                     <span v-if="version.publié && version.date_publication">
                       • Publiée le {{ formatDate(version.date_publication) }}
                     </span>
                     <span class="badge bg-info ms-2">
                       {{ version.memo_count || 0 }} mémos
                      </span>
                    </p>
                  </div>
                  <p class="card-text">{{ version.latest_memo || 'Aucun mémo disponible' }}</p>
                 <div class="version-actions">
                   <button 
                     class="btn btn-sm btn-outline-info"
                     @click="viewVersionDetails(version.id)"
                     title="Voir les détails"
                   >
                     🔍 Détails
                   </button>
                   <button 
                     v-if="!version.publié"
                     class="btn btn-sm btn-success"
                     @click="publishVersion(version.id)"
                     title="Publier"
                   >
                     ✓ Publier
                   </button>
                   
                 </div>
               </div>
               
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de création/édition de version -->
    <b-modal
      v-model="showCreateModal"
      :title="isEditing ? 'Modifier la version' : 'Nouvelle version'"
      size="lg"
      @ok="saveVersion"
      @cancel="resetForm"
      @hidden="resetForm"
    >
      <b-form @submit.prevent="saveVersion">
        <div class="row">
          <div class="col-md-4">
            <b-form-group label="Version majeure *" label-for="v_max">
              <b-form-input 
                id="v_max"
                v-model.number="form.v_max"
                type="number"
                min="0"
                required
              />
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group label="Version mineure *" label-for="v_mid">
              <b-form-input 
                id="v_mid"
                v-model.number="form.v_mid"
                type="number"
                min="0"
                required
              />
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group label="Version patch *" label-for="v_min">
              <b-form-input 
                id="v_min"
                v-model.number="form.v_min"
                type="number"
                min="0"
                required
              />
            </b-form-group>
          </div>
        </div>

        <b-form-group label="Titre *" label-for="title">
          <b-form-input 
            id="title"
            v-model="form.title"
            placeholder="Titre de la version"
            required
          />
        </b-form-group>

        <b-form-group label="Description *" label-for="description">
          <b-form-textarea 
            id="description"
            v-model="form.description"
            placeholder="Description détaillée de la version"
            rows="4"
            required
          />
        </b-form-group>

        <b-form-group label="Statut" label-for="publié">
          <b-form-checkbox 
            id="publié"
            v-model="form.publié"
          >
            Publier immédiatement
          </b-form-checkbox>
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button variant="secondary" @click="resetForm">
          Annuler
        </b-button>
        <b-button variant="primary" @click="saveVersion" :disabled="!isFormValid">
          {{ isEditing ? 'Modifier' : 'Créer' }}
        </b-button>
      </template>
    </b-modal>

         <!-- Modal des logs -->
     <b-modal
       v-model="showLogsModal"
       title="Historique des modifications"
       size="lg"
       hide-footer
     >
       <div class="d-flex justify-content-between align-items-center mb-3">
         <h6 class="mb-0">Mémos de la version</h6>
         <button 
           class="btn btn-sm btn-outline-primary"
           @click="addMemoFromLogs"
         >
           📝 Ajouter un mémo
         </button>
       </div>
       
       <div v-if="logs.length === 0" class="text-center">
         <p class="text-muted">Aucun mémo trouvé pour cette version.</p>
         <button 
           class="btn btn-outline-primary"
           @click="addMemoFromLogs"
         >
           📝 Ajouter le premier mémo
         </button>
       </div>
               <div v-else class="logs-list">
          <div 
            v-for="log in logs" 
            :key="log.id"
            class="log-item"
          >
            <p class="mb-2">{{ log.memo }}</p>
          </div>
        </div>
     </b-modal>

     <!-- Modal d'ajout de mémo -->
     <b-modal
       v-model="showAddMemoModal"
       title="Ajouter un mémo"
       size="lg"
       @ok="saveMemo"
       @cancel="resetMemoForm"
       @hidden="resetMemoForm"
       :ok-disabled="!memoForm.memo.trim()"
     >
       <b-form @submit.prevent="saveMemo">
         <b-form-group label="Mémo *" label-for="memo">
           <b-form-textarea 
             id="memo"
             v-model="memoForm.memo"
             placeholder="Description du mémo..."
             rows="4"
             required
           />
         </b-form-group>
       </b-form>

                <template #modal-footer>
           <b-button variant="secondary" @click="resetMemoForm">
             Annuler
           </b-button>
           <b-button variant="primary" @click="saveMemo" :disabled="!memoForm.memo.trim() || !selectedVersionId">
             Ajouter
           </b-button>
         </template>
     </b-modal>

     <!-- Modal des détails de version -->
     <b-modal
       v-model="showDetailsModal"
       title="Détails de la version"
       size="xl"
       hide-footer
     >
       <div v-if="selectedVersion" class="version-details">
         <!-- En-tête de la version -->
         <div class="version-header-details mb-4">
           <h4>{{ selectedVersion.title }}</h4>
           <div class="version-meta">
             <span class="badge bg-primary me-2">Version {{ selectedVersion.version_number }}</span>
             <span :class="getStatusBadgeClass(selectedVersion.publié)" class="badge me-2">
               {{ selectedVersion.publié ? 'Publiée' : 'Brouillon' }}
             </span>
             <span class="badge bg-info me-2">{{ selectedVersion.memo_count || 0 }} mémos</span>
           </div>
           <p class="text-muted mt-2">
             Créée le {{ formatDate(selectedVersion.release_date) }}
             <span v-if="selectedVersion.publié && selectedVersion.date_publication">
               • Publiée le {{ formatDate(selectedVersion.date_publication) }}
             </span>
           </p>
         </div>

         <!-- Actions de la version -->
         <div class="version-actions-details mb-4">
           <div class="d-flex flex-wrap gap-2">
             <button 
               v-if="!selectedVersion.publié"
               class="btn btn-success"
               @click="publishVersionFromDetails(selectedVersion.id)"
               title="Publier"
             >
               ✓ Publier
             </button>
             <button 
               v-else
               class="btn btn-warning"
               @click="unpublishVersionFromDetails(selectedVersion.id)"
               title="Dé-publier"
             >
               👁️ Dé-publier
             </button>
             <button 
               class="btn btn-outline-primary"
               @click="editVersionFromDetails(selectedVersion)"
               title="Modifier"
             >
               ✏️ Modifier
             </button>
             <button 
               class="btn btn-outline-secondary"
               @click="addMemoFromDetails(selectedVersion.id)"
               title="Ajouter un mémo"
             >
               📝 Ajouter mémo
             </button>
             <button 
               class="btn btn-outline-danger"
               @click="deleteVersionFromDetails(selectedVersion.id)"
               title="Supprimer"
             >
               🗑️ Supprimer
             </button>
           </div>
         </div>

                   <!-- Liste des mémos -->
          <div class="memos-section">
            <h5>Historique des mémos</h5>
            <div v-if="logs.length === 0" class="text-center py-4">
              <p class="text-muted">Aucun mémo trouvé pour cette version.</p>
              <button 
                class="btn btn-outline-primary"
                @click="addMemoFromDetails(selectedVersion.id)"
              >
                📝 Ajouter le premier mémo
              </button>
            </div>
            <div v-else class="logs-list">
              <div 
                v-for="log in logs" 
                :key="log.id"
                class="log-item"
              >
                <p class="mb-2">{{ log.memo }}</p>
              </div>
            </div>
          </div>
       </div>
     </b-modal>
   </div>
 </template>

<script>
import api from '@/plugins/api';

export default {
  name: 'AdminVersionsPage',
  data() {
    return {
      versions: [],
      logs: [],
      loading: true,
             showCreateModal: false,
       showLogsModal: false,
       showAddMemoModal: false,
       showDetailsModal: false,
       isEditing: false,
       editingId: null,
       selectedVersionId: null,
       selectedVersion: null,
      form: {
        v_max: 1,
        v_mid: 0,
        v_min: 0,
        title: '',
        description: '',
        publié: false
      },
      memoForm: {
        memo: ''
      }
    };
  },
  
  computed: {
    isFormValid() {
      return this.form.v_max >= 0 && 
             this.form.v_mid >= 0 && 
             this.form.v_min >= 0 && 
             this.form.title && 
             this.form.description;
    }
  },
  
  async mounted() {
    await this.loadVersions();
  },
  
  methods: {
    async loadVersions() {
      try {
        this.loading = true;
        const { data } = await api.get('/versions/admin/all');
        this.versions = data;
      } catch (error) {
        console.error('Erreur lors du chargement des versions:', error);
      } finally {
        this.loading = false;
      }
    },

    openCreateModal() {
      this.isEditing = false;
      this.editingId = null;
      this.resetForm();
      
      // Pré-remplir avec la prochaine version
      if (this.versions.length > 0) {
        const latestVersion = this.versions[0];
        this.form.v_max = latestVersion.v_max;
        this.form.v_mid = latestVersion.v_mid;
        this.form.v_min = latestVersion.v_min + 1;
      } else {
        this.form.v_max = 1;
        this.form.v_mid = 0;
        this.form.v_min = 0;
      }
      
      this.showCreateModal = true;
    },

    editVersion(version) {
      this.isEditing = true;
      this.editingId = version.id;
      this.form = {
        v_max: version.v_max,
        v_mid: version.v_mid,
        v_min: version.v_min,
        title: version.title,
        description: version.description,
        publié: version.publié
      };
      this.showCreateModal = true;
    },

         async deleteVersion(id) {
       if (!confirm('Êtes-vous sûr de vouloir supprimer cette version ?')) {
         return false;
       }

       try {
         await api.delete(`/versions/${id}`);
         await this.loadVersions();
         return true;
       } catch (error) {
         console.error('Erreur lors de la suppression:', error);
         alert('Erreur lors de la suppression de la version');
         return false;
       }
     },

    async publishVersion(id) {
      try {
        await api.post(`/versions/${id}/publish`);
        await this.loadVersions();
      } catch (error) {
        console.error('Erreur lors de la publication:', error);
        alert('Erreur lors de la publication de la version');
      }
    },

    async unpublishVersion(id) {
      try {
        await api.post(`/versions/${id}/unpublish`);
        await this.loadVersions();
      } catch (error) {
        console.error('Erreur lors de la dé-publication:', error);
        alert('Erreur lors de la dé-publication de la version');
      }
    },

         async viewLogs(versionId) {
       try {
         this.selectedVersionId = versionId;
         const { data } = await api.get(`/versions/${versionId}/logs`);
         this.logs = data;
         this.showLogsModal = true;
       } catch (error) {
         console.error('Erreur lors du chargement des logs:', error);
         alert('Erreur lors du chargement des logs');
       }
     },

     addMemo(versionId) {
       if (!versionId) {
         console.error('VersionId est null ou undefined');
         alert('Erreur: ID de version manquant');
         return;
       }
       this.selectedVersionId = versionId;
       this.resetMemoForm();
       this.showAddMemoModal = true;
     },

     async saveMemo() {
       if (!this.memoForm.memo.trim()) {
         return;
       }

       if (!this.selectedVersionId) {
         alert('Erreur: ID de version manquant');
         return;
       }

       try {
         await api.post(`/versions/${this.selectedVersionId}/memo`, {
           memo: this.memoForm.memo
         });
         
         this.showAddMemoModal = false;
         this.resetMemoForm();
         
         // Recharger les versions pour mettre à jour l'affichage
         await this.loadVersions();
         
         // Si les logs sont ouverts, les recharger aussi
         if (this.showLogsModal) {
           await this.viewLogs(this.selectedVersionId);
         }
       } catch (error) {
         console.error('Erreur lors de l\'ajout du mémo:', error);
         alert('Erreur lors de l\'ajout du mémo');
       }
     },

     resetMemoForm() {
       this.memoForm.memo = '';
       // Ne pas réinitialiser selectedVersionId ici pour éviter les problèmes
       // this.selectedVersionId = null;
     },

     addMemoFromLogs() {
       // Fermer la modal des logs et ouvrir celle d'ajout de mémo
       this.showLogsModal = false;
       // Utiliser setTimeout pour s'assurer que la modal se ferme avant d'en ouvrir une autre
       setTimeout(() => {
         this.addMemo(this.selectedVersionId);
       }, 100);
     },

     async viewVersionDetails(versionId) {
       try {
         this.selectedVersionId = versionId;
         // Trouver la version dans la liste
         this.selectedVersion = this.versions.find(v => v.id === versionId);
         if (!this.selectedVersion) {
           alert('Version non trouvée');
           return;
         }
         
         // Charger les logs
         const { data } = await api.get(`/versions/${versionId}/logs`);
         this.logs = data;
         this.showDetailsModal = true;
       } catch (error) {
         console.error('Erreur lors du chargement des détails:', error);
         alert('Erreur lors du chargement des détails');
       }
     },

     // Méthodes pour les actions depuis la modal des détails
     async publishVersionFromDetails(id) {
       await this.publishVersion(id);
       this.selectedVersion.publié = true;
     },

     async unpublishVersionFromDetails(id) {
       await this.unpublishVersion(id);
       this.selectedVersion.publié = false;
     },

     editVersionFromDetails(version) {
       this.showDetailsModal = false;
       setTimeout(() => {
         this.editVersion(version);
       }, 100);
     },

     addMemoFromDetails(versionId) {
       this.showDetailsModal = false;
       setTimeout(() => {
         this.addMemo(versionId);
       }, 100);
     },

     async deleteVersionFromDetails(id) {
       if (await this.deleteVersion(id)) {
         this.showDetailsModal = false;
       }
     },

    async saveVersion() {
      if (!this.isFormValid) {
        return;
      }

      try {
        const versionData = {
          version_number: `${this.form.v_max}.${this.form.v_mid}.${this.form.v_min}`,
          title: this.form.title,
          description: this.form.description,
          publié: this.form.publié
        };

        if (this.isEditing) {
          await api.put(`/versions/${this.editingId}`, versionData);
        } else {
          await api.post('/versions/', versionData);
        }

        this.showCreateModal = false;
        this.resetForm();
        await this.loadVersions();
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        alert('Erreur lors de la sauvegarde de la version');
      }
    },

    resetForm() {
      this.isEditing = false;
      this.editingId = null;
      this.form = {
        v_max: 1,
        v_mid: 0,
        v_min: 0,
        title: '',
        description: '',
        publié: false
      };
    },

    getStatusBadgeClass(publié) {
      return publié ? 'bg-success' : 'bg-secondary';
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.admin-versions-page {
  padding-bottom: 2rem;
}

.admin-versions-container {
  max-width: 900px;
  margin: 0 auto;
}

.header-section {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.5rem;
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.header-title-section {
  text-align: center;
}

.header-title {
  color: #2c3e50;
  font-weight: 600;
  margin: 0;
  font-size: 1.75rem;
}

.header-actions-section {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.new-version-btn {
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  font-size: 1rem;
}

.new-version-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.version-item {
  transition: transform 0.2s, box-shadow 0.2s;
}

.version-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.version-header h5 {
  color: #333;
  margin-bottom: 0.5rem;
}

.version-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.version-header {
  flex: 1;
}

.version-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-start;
}

.version-actions .btn {
  flex-shrink: 0;
  min-width: 80px;
}

/* Responsive design pour les boutons */
@media (max-width: 768px) {
  .header-section {
    padding: 1rem;
  }
  
  .header-title {
    font-size: 1.5rem;
  }
  
  .new-version-btn {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
  }
  
  .version-actions {
    justify-content: center;
    gap: 0.25rem;
  }
  
  .version-actions .btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    min-width: 70px;
  }
}

@media (max-width: 576px) {
  .header-section {
    padding: 0.75rem;
  }
  
  .header-title {
    font-size: 1.25rem;
  }
  
  .new-version-btn {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }
  
  .version-actions {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
  
  .version-actions .btn {
    width: 100%;
    justify-content: center;
    min-width: auto;
  }
}

.logs-list {
  max-height: 400px;
  overflow-y: auto;
}

.log-item:last-child {
  border-bottom: none !important;
}
</style>
